import React, { useState, useEffect, useCallback } from "react";
import { useAuth } from "./hooks/useAuth";
import axios from "axios";
import { API } from "./hooks/useApi";
import "./responsive.css";

const DOCKER = axios.create({ baseURL: "/" });
import LoginPage        from "./components/LoginPage";
import Header           from "./components/Header";
import MacroPanel       from "./components/MacroPanel";
import MarketNarrative  from "./components/MarketNarrative";
import AssetGrid        from "./components/AssetGrid";
import GateStatus       from "./components/GateStatus";
import LivePositions    from "./components/LivePositions";
import PositionProjection from "./components/PositionProjection";
import SignalFeed       from "./components/SignalFeed";
import Headlines        from "./components/Headlines";
import EquityCurve      from "./components/EquityCurve";
import RiskPanel        from "./components/RiskPanel";
import ActivityFeed     from "./components/ActivityFeed";
import TradeHistory     from "./components/TradeHistory";
import ReturnsPanel     from "./components/ReturnsPanel";
import UserManagement   from "./components/UserManagement";
import SystemControl, { MaintenanceBanner } from "./components/SystemControl";
import MT5Terminal      from "./components/MT5Terminal";
import Settings         from "./components/Settings";

const page = {
  minHeight: "100vh",
  background: "#080c18",
  color: "#e2e8f0",
  fontFamily: "'Segoe UI', system-ui, -apple-system, sans-serif",
};

export default function App() {
  const [tab, setTab] = useState("live");
  const { user, login, logout, setup } = useAuth();
  const [brainOnline, setBrainOnline] = useState(true);

  const checkBrain = useCallback(async () => {
    // The brain's own API is ground truth: a healthy /health means the brain is
    // up regardless of what the docker-inspect sidecar reports. Only when /health
    // is unreachable do we consult the sidecar, and even then a 200 {running:false}
    // is treated as offline only if the direct probe also failed. This avoids the
    // old bug where a transient docker-inspect failure (200 {running:false}) flipped
    // a perfectly healthy brain to "offline".
    try {
      const res = await API.get("/health");
      setBrainOnline(res.data?.status === "ok");
    } catch {
      try {
        const res = await DOCKER.get("/docker/brain/health");
        setBrainOnline(res.data?.running ?? false);
      } catch { setBrainOnline(false); }
    }
  }, []);

  useEffect(() => {
    if (!user) return;
    checkBrain();
    const t = setInterval(checkBrain, 10000);
    return () => clearInterval(t);
  }, [user, checkBrain]);

  if (!user) return <LoginPage onLogin={login} onSetup={setup} />;

  const isAdmin = user.role === "admin";

  if (!brainOnline && !isAdmin) {
    return (
      <div style={page}>
        <Header tab={tab} setTab={setTab} user={user} onLogout={logout} brainOnline={false} />
        <MaintenanceBanner />
      </div>
    );
  }

  return (
    <div style={page}>
      <Header tab={tab} setTab={setTab} user={user} onLogout={logout} brainOnline={brainOnline} />

      {tab === "live" && brainOnline && (
        <>
          <div style={{ padding: "10px 10px 0" }}><MacroPanel /></div>
          <div style={{ padding: "6px 10px 0" }}><MarketNarrative /></div>
          {/* Responsive 3-col → 1-col grid */}
          <div className="sentinel-grid">
            <div className="sentinel-col"><GateStatus /><SignalFeed /><Headlines /></div>
            <div className="sentinel-col"><AssetGrid /><LivePositions /><EquityCurve /></div>
            <div className="sentinel-col"><RiskPanel /><PositionProjection /><ActivityFeed /></div>
          </div>
        </>
      )}

      {tab === "live" && !brainOnline && isAdmin && (
        <div style={{ padding: "20px", textAlign: "center" }}>
          <MaintenanceBanner />
          <div style={{ marginTop: "20px", fontSize: "13px", color: "#60a5fa" }}>
            Use the ⚙️ System tab to start the Brain.
          </div>
        </div>
      )}

      {tab === "history" && <div style={{ padding: "10px" }}><TradeHistory limit={200} /></div>}
      {tab === "returns" && <ReturnsPanel />}
      {tab === "users"   && isAdmin && <UserManagement />}
      {tab === "mt5"     && isAdmin && <MT5Terminal />}
      {tab === "system"  && isAdmin && <SystemControl />}
      {tab === "settings" && isAdmin && <Settings />}
    </div>
  );
}
